/**
 * Reads the motion sensors for the recording screen (todo 7). On Android the native MotionRecorder plugin
 * (android/.../MotionRecorderPlugin.java) samples at the fastest rate and counts the volume keys as taps. In the browser
 * (todo 15: testing from the web app without a new APK) DeviceMotionEvent gives what the browser allows: about 60 Hz
 * in Chrome on Android, nothing on a desktop or on an iPhone without permission. `src/platform.ts` picks the source.
 */
import { registerPlugin } from "@capacitor/core";
import type { Columns, Mark, MarkKind, RawRecording } from "../core/motion";

export interface MotionSource {
  /** starts sampling; the recording's clock starts here */
  start(): Promise<void>;
  mark(kind: MarkKind): void;
  /** samples so far, for the live display */
  status(): Promise<{ accel: number; gyro: number }>;
  /**
   * The accelerometer samples from index `from` on, while recording (the live rep count, todo 8). Rejects on an APK
   * older than 0.3.6, whose plugin hands the samples over only at the end.
   */
  read(from: number): Promise<Columns>;
  stop(): Promise<RawRecording>;
  /** a volume key was pressed (the tap is already marked); returns the cleanup */
  onVolumeTap(fn: () => void): () => void;
}

interface NativeResult {
  accel: Columns;
  gyro?: Columns | null;
  marks: Mark[];
  device: Record<string, string>;
}

const MotionRecorder = registerPlugin<{
  start(): Promise<{ gyro: boolean }>;
  mark(o: { kind: MarkKind }): Promise<void>;
  status(): Promise<{ accel: number; gyro: number }>;
  read(o: { from: number }): Promise<{ accel: Columns }>;
  stop(): Promise<NativeResult>;
  addListener(event: "volumeTap", fn: (d: { count: number }) => void): Promise<{ remove(): Promise<void> }>;
}>("MotionRecorder");

export const nativeMotionSource: MotionSource = {
  async start() { await MotionRecorder.start(); },
  mark(kind) { void MotionRecorder.mark({ kind }); },
  status: () => MotionRecorder.status(),
  read: (from) => MotionRecorder.read({ from }).then((r) => r.accel),
  async stop() {
    const r = await MotionRecorder.stop();
    return { accel: r.accel, gyro: r.gyro ?? null, marks: r.marks, source: "android", device: r.device };
  },
  onVolumeTap(fn) {
    const l = MotionRecorder.addListener("volumeTap", () => fn());
    return () => void l.then((x) => x.remove());
  },
};

const emptyColumns = (): Columns => ({ t: [], x: [], y: [], z: [] });
const DEG = Math.PI / 180;

type PermissionRequest = () => Promise<"granted" | "denied">;
/** Safari (iOS 13+) only sends devicemotion after this was granted from a tap; other browsers do not have it. */
const permissionRequest = () =>
  (globalThis.DeviceMotionEvent as unknown as { requestPermission?: PermissionRequest } | undefined)?.requestPermission;

/**
 * Asks for the motion permission where the browser wants that. Call it in a tap handler before anything is awaited
 * (Safari refuses it outside a tap); resolves false when refused.
 */
export function askMotionPermission(): Promise<boolean> {
  const ask = permissionRequest();
  if (!ask) return Promise.resolve(true);
  return ask.call(DeviceMotionEvent).then((r) => r === "granted", () => false);
}

let probed: Promise<boolean> | undefined;

/**
 * Listens for a moment whether devicemotion events with values arrive (a desktop Chrome sends one empty event, or
 * none). A success is kept; a failure is tried again on the next call.
 */
export function probeWebMotion(waitMs = 1000): Promise<boolean> {
  if (typeof window === "undefined" || !("DeviceMotionEvent" in window)) return Promise.resolve(false);
  probed ??= new Promise<boolean>((resolve) => {
    const done = (ok: boolean) => {
      window.removeEventListener("devicemotion", onMotion);
      clearTimeout(timer);
      if (!ok) probed = undefined;
      resolve(ok);
    };
    const onMotion = (e: DeviceMotionEvent) => { if (e.accelerationIncludingGravity?.x != null) done(true); };
    const timer = setTimeout(() => done(false), waitMs);
    window.addEventListener("devicemotion", onMotion);
  });
  return probed;
}

/**
 * The browser's motion source on DeviceMotionEvent, in the same recording format as the native one: the accelerometer
 * including gravity (m/s², the phone's axes as on Android) and the rotation rate (deg/s around z, x, y as alpha, beta,
 * gamma; stored as rad/s in x/y/z like Android). The clock is the events' `timeStamp`, the same as `performance.now()`.
 * The event's `interval` (the rate the browser promises) goes into the device info; the measured rate is in the file.
 */
export function webMotionSource(): MotionSource {
  let t0 = 0;
  let accel = emptyColumns();
  let gyro = emptyColumns();
  let marks: Mark[] = [];
  let intervalMs = 0;
  const onMotion = (e: DeviceMotionEvent) => {
    const t = e.timeStamp - t0;
    // an event queued before the start belongs to no recording
    if (t < 0) return;
    if (e.interval) intervalMs = e.interval;
    const a = e.accelerationIncludingGravity;
    if (a?.x != null && a.y != null && a.z != null) {
      accel.t.push(t); accel.x.push(a.x); accel.y.push(a.y); accel.z.push(a.z);
    }
    const r = e.rotationRate;
    if (r?.alpha != null && r.beta != null && r.gamma != null) {
      gyro.t.push(t); gyro.x.push(r.beta * DEG); gyro.y.push(r.gamma * DEG); gyro.z.push(r.alpha * DEG);
    }
  };
  return {
    // the permission was asked by `platform.motion()`, from the tap
    async start() {
      accel = emptyColumns(); gyro = emptyColumns(); marks = []; intervalMs = 0;
      t0 = performance.now();
      window.addEventListener("devicemotion", onMotion);
    },
    mark(kind) { marks.push({ kind, t: performance.now() - t0 }); },
    async status() { return { accel: accel.t.length, gyro: gyro.t.length }; },
    async read(from) {
      return { t: accel.t.slice(from), x: accel.x.slice(from), y: accel.y.slice(from), z: accel.z.slice(from) };
    },
    async stop() {
      window.removeEventListener("devicemotion", onMotion);
      const device: Record<string, string> = { api: "DeviceMotionEvent", userAgent: navigator.userAgent };
      if (intervalMs) {
        device.intervalMs = String(intervalMs);
        device.intervalHz = String(Math.round(1000 / intervalMs));
      }
      return { accel, gyro: gyro.t.length ? gyro : null, marks, source: "web", device };
    },
    onVolumeTap: () => () => undefined,
  };
}
